import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { Lock, Eye, EyeOff } from "lucide-react";
import { AuthApi } from "../lib/adminApi";

export default function AdminLogin() {
    const [password, setPassword] = useState("");
    const [show, setShow] = useState(false);
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    if (AuthApi.hasToken()) return <Navigate to="/admin" replace />;

    const submit = async (e) => {
        e.preventDefault();
        if (!password) return;
        setLoading(true);
        try {
            const { data } = await AuthApi.login(password);
            AuthApi.saveToken(data.token);
            toast.success("Acceso concedido");
            navigate("/admin", { replace: true });
        } catch (err) {
            const detail =
                err?.response?.data?.detail || "No se pudo iniciar sesión";
            toast.error(
                typeof detail === "string" ? detail : "Contraseña incorrecta",
            );
        } finally {
            setLoading(false);
        }
    };

    return (
        <div
            className="min-h-screen bg-cream flex items-center justify-center px-6"
            data-testid="admin-login-page"
        >
            <div className="absolute top-0 left-0 right-0 h-px bg-navy/10" />

            <div className="w-full max-w-md">
                <div className="text-center mb-10">
                    <a
                        href="/"
                        className="inline-block font-display text-2xl text-navy"
                    >
                        Gilberto<span className="text-warm-600">.</span>Veliz
                    </a>
                    <div className="mt-2 text-[10px] uppercase tracking-[0.3em] text-navy/50">
                        Admin Access
                    </div>
                </div>

                <div className="bg-white border border-navy/15 p-8 md:p-10">
                    <div className="flex items-center gap-3 mb-6">
                        <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-warm">
                            <Lock className="w-4 h-4 text-navy" strokeWidth={2} />
                        </span>
                        <h1 className="font-display text-3xl text-navy">
                            Ingresar
                        </h1>
                    </div>
                    <p className="text-sm text-navy/60 mb-6">
                        Panel privado para gestionar los mensajes recibidos.
                    </p>

                    <form onSubmit={submit} className="space-y-6" data-testid="admin-login-form">
                        <div>
                            <label className="block text-[10px] uppercase tracking-[0.25em] text-navy/60 mb-2">
                                Contraseña
                            </label>
                            <div className="relative">
                                <input
                                    type={show ? "text" : "password"}
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    className="editorial-input pr-10"
                                    autoFocus
                                    data-testid="admin-password-input"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShow((s) => !s)}
                                    className="absolute right-0 top-1/2 -translate-y-1/2 text-navy/40 hover:text-navy p-1"
                                    aria-label="Toggle password visibility"
                                    data-testid="toggle-password-visibility"
                                >
                                    {show ? (
                                        <EyeOff className="w-4 h-4" />
                                    ) : (
                                        <Eye className="w-4 h-4" />
                                    )}
                                </button>
                            </div>
                        </div>

                        <button
                            type="submit"
                            disabled={loading || !password}
                            className="w-full bg-navy text-cream px-6 py-3.5 text-sm tracking-wide hover:bg-warm-600 transition-colors disabled:opacity-60"
                            data-testid="admin-login-submit"
                        >
                            {loading ? "Verificando…" : "Entrar →"}
                        </button>
                    </form>
                </div>

                <div className="mt-6 text-center">
                    <a
                        href="/"
                        className="text-xs text-navy/50 hover:text-navy"
                        data-testid="back-to-site"
                    >
                        ← Volver al sitio
                    </a>
                </div>
            </div>
        </div>
    );
}
